import type { ObjectInventoryField } from '@/entities/Inventory/ObjectTypes'
import {
  objectPassportFieldsToInventoryFields,
  objectPassportValuesToRuntimeEntries,
  shouldUseNormalizedObjectPassport,
} from '@/entities/Inventory/ObjectPassportFields/model/objectPassportFieldAdapter'
import type {
  ObjectPassportFieldValueInput,
  ObjectPassportFieldsPayload,
} from '@/entities/Inventory/ObjectPassportFields/model/types'
import {
  createRuntimeFieldValueMap,
  type RuntimeFieldValueMap,
} from '@/features/Inventory/ObjectListEdit/model/runtimeFieldBindings'

export type { ObjectPassportFieldValueInput } from '@/entities/Inventory/ObjectPassportFields'

export {
  objectPassportFieldsToInventoryFields,
  objectPassportValueToEditScalar,
  objectPassportValueToRuntimeScalar,
  objectPassportValuesToRuntimeEntries,
  shouldUseNormalizedObjectPassport,
} from '@/entities/Inventory/ObjectPassportFields/model/objectPassportFieldAdapter'

/**
 * Строит RuntimeFieldValueMap из нормализованного ответа и метаданных полей.
 */
export function buildRuntimeFieldValueMapFromObjectPassport(
  fieldMetadata: ObjectInventoryField[] | null | undefined,
  payload: ObjectPassportFieldsPayload,
  mode: 'display' | 'edit' = 'edit',
): RuntimeFieldValueMap {
  return createRuntimeFieldValueMap(
    fieldMetadata,
    objectPassportValuesToRuntimeEntries(payload.values, mode),
  )
}

const LIST_CARDINALITIES = new Set(['one_to_many', 'many_to_many'])

function parseValueList(raw: unknown): number[] | null {
  if (raw == null || raw === '') return null
  const ids = String(raw)
    .split(',')
    .map((s) => Number(s.trim()))
    .filter((n) => Number.isFinite(n) && n > 0)
  return ids.length > 0 ? ids : null
}

/**
 * Готовит тело PUT /objects/:id/passport для сохранения скалярных значений.
 */
export function buildObjectPassportFieldValuesPayload(
  fieldValues: RuntimeFieldValueMap,
  fieldMetadata: ObjectInventoryField[] | null | undefined,
): ObjectPassportFieldValueInput[] {
  return (fieldMetadata ?? [])
    .map((field) => {
      const fieldId = Number(field.id)
      if (!Number.isFinite(fieldId) || fieldId <= 0) return null

      const raw = fieldValues[String(field.id)]

      if (
        LIST_CARDINALITIES.has(field.cardinality ?? '') ||
        LIST_CARDINALITIES.has(field.type)
      ) {
        return {
          field_id: fieldId,
          value: null,
          value_list: parseValueList(raw),
        }
      }

      const value = raw == null || raw === '' ? null : String(raw)

      return {
        field_id: fieldId,
        value,
      }
    })
    .filter((item): item is ObjectPassportFieldValueInput => item != null)
}
